import { DisclosureChevron } from '@affine/core/components/animation/disclosure-chevron';
import {
  UiNavigationItem,
  useUiNavigationEntry,
} from '@affine/core/modules/streamlain/copilot/ui-navigation-item';
import { WorkbenchLink } from '@affine/core/modules/workbench/view/workbench-link';
import clsx from 'clsx';
import React, { type SVGAttributes } from 'react';
import type { To } from 'react-router-dom';

import * as styles from './index.css';

export interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactElement<SVGAttributes<SVGElement>>;
  active?: boolean;
  disabled?: boolean;
  // true, false, undefined. undefined means no collapse
  collapsed?: boolean;
  // if onCollapsedChange is given, but collapsed is undefined, then we will render the collapse button as disabled
  onCollapsedChange?: (collapsed: boolean) => void;
  postfix?: React.ReactElement;
  postfixDisplay?: 'always' | 'hover';
  /** Link wrapper owns keyboard and link semantics. */
  linkChild?: boolean;
}

export interface MenuLinkItemProps extends MenuItemProps {
  to: To;
  linkComponent?: React.ComponentType<
    React.PropsWithChildren<{
      className?: string;
      draggable?: boolean;
      to: To;
    }>
  >;
}

const stopPropagation: React.MouseEventHandler = e => {
  e.stopPropagation();
};

const HOVER_SUPPRESSION_RESET_DISTANCE = 2;

/**
 * This component is not a generic component.
 * It is used for the app sidebar.
 */
export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
  (
    {
      onClick,
      onKeyDown,
      onPointerLeave,
      onPointerMove,
      icon,
      active,
      children,
      disabled,
      collapsed,
      onCollapsedChange,
      postfix,
      postfixDisplay = 'hover',
      linkChild = false,
      ...props
    },
    ref
  ) => {
    const navigation = useUiNavigationEntry();
    const collapsible = onCollapsedChange !== undefined;
    const [postfixHoverSuppressed, setPostfixHoverSuppressed] =
      React.useState(false);
    const hoverSuppressionOriginRef = React.useRef<{
      x: number;
      y: number;
    } | null>(null);

    const clearPostfixHoverSuppression = React.useCallback(() => {
      hoverSuppressionOriginRef.current = null;
      setPostfixHoverSuppressed(false);
    }, []);

    const handlePointerMove = React.useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        onPointerMove?.(event);
        if (!postfixHoverSuppressed) {
          return;
        }

        const origin = hoverSuppressionOriginRef.current;
        if (
          !origin ||
          Math.abs(event.clientX - origin.x) >=
            HOVER_SUPPRESSION_RESET_DISTANCE ||
          Math.abs(event.clientY - origin.y) >= HOVER_SUPPRESSION_RESET_DISTANCE
        ) {
          clearPostfixHoverSuppression();
        }
      },
      [clearPostfixHoverSuppression, onPointerMove, postfixHoverSuppressed]
    );

    const handlePointerLeave = React.useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        onPointerLeave?.(event);
        clearPostfixHoverSuppression();
      },
      [clearPostfixHoverSuppression, onPointerLeave]
    );

    const handlePostfixClickCapture = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        hoverSuppressionOriginRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
        setPostfixHoverSuppressed(true);
      },
      []
    );

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(event);
        if (
          event.defaultPrevented ||
          disabled ||
          linkChild ||
          (!onClick && !navigation) ||
          (event.key !== 'Enter' && event.key !== ' ')
        ) {
          return;
        }
        event.preventDefault();
        event.currentTarget.click();
      },
      [disabled, linkChild, navigation, onClick, onKeyDown]
    );

    const keyboardInteractive =
      Boolean(onClick || navigation) && !disabled && !linkChild;

    const item = (
      <div
        ref={ref}
        {...props}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        onPointerLeave={handlePointerLeave}
        onPointerMove={handlePointerMove}
        className={clsx([styles.root, props.className])}
        data-active={active}
        data-disabled={disabled}
        data-collapsible={collapsible}
        data-hover-suppressed={postfixHoverSuppressed}
        role={
          props.role ??
          ((onClick || navigation) && !linkChild ? 'button' : undefined)
        }
        tabIndex={props.tabIndex ?? (keyboardInteractive ? 0 : undefined)}
        aria-disabled={disabled || undefined}
      >
        {icon && (
          <div className={styles.iconsContainer} data-collapsible={collapsible}>
            {collapsible && (
              <div
                data-disabled={collapsed === undefined ? true : undefined}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault(); // for links
                  onCollapsedChange?.(!collapsed);
                }}
                data-testid="fav-collapsed-button"
                className={styles.collapsedIconContainer}
              >
                <DisclosureChevron
                  className={styles.collapsedIcon}
                  expanded={collapsed === false}
                />
              </div>
            )}
            {React.cloneElement(icon, {
              className: clsx([styles.icon, icon.props.className]),
            })}
          </div>
        )}

        <div className={styles.content}>{children}</div>
        {postfix ? (
          <div
            className={styles.postfix}
            data-postfix-display={postfixDisplay}
            data-sidebar-action-reveal-fade
            onClick={stopPropagation}
            onClickCapture={handlePostfixClickCapture}
          >
            {postfix}
          </div>
        ) : null}
      </div>
    );
    return navigation && !linkChild ? (
      <UiNavigationItem {...navigation}>{item}</UiNavigationItem>
    ) : (
      item
    );
  }
);
MenuItem.displayName = 'MenuItem';

export const MenuLinkItem = React.forwardRef<HTMLDivElement, MenuLinkItemProps>(
  ({ to, linkComponent: LinkComponent = WorkbenchLink, ...props }, ref) => {
    const navigation = useUiNavigationEntry();
    const geometryRef = React.useRef<HTMLDivElement | null>(null);
    const innerRef = React.useCallback(
      (element: HTMLDivElement | null) => {
        geometryRef.current = element;
        if (typeof ref === 'function') return ref(element);
        if (ref) ref.current = element;
      },
      [ref]
    );
    const link = (
      <LinkComponent to={to} className={styles.linkItemRoot} draggable={false}>
        {/* The actual anchor is clickable; its display:contents child owns geometry and existing DnD refs. */}
        <MenuItem ref={innerRef} linkChild {...props} />
      </LinkComponent>
    );
    return navigation ? (
      <UiNavigationItem {...navigation} geometryRef={geometryRef}>
        {link}
      </UiNavigationItem>
    ) : (
      link
    );
  }
);
MenuLinkItem.displayName = 'MenuLinkItem';
